import React, { createContext, useCallback, useMemo, useState } from 'react';
import proptypes from 'prop-types';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import { useGroupedPromotionProducts } from '../hooks/useGroupedPromotionProducts';
import { FEATURE_SWITCH_KEYS } from '../utils/constants';

const { bool, node, shape, arrayOf, string, number } = proptypes;

const EMPTY_OBJECT = Object.freeze({});

const defaultGroupedPromoContext = Object.freeze({
  isDisabled: false,
  items: {}, // Dictionary format: { promotionId: [{itemId, quantity}, ...] }
  // Primary: items grouped by anchor ID for cart/business logic
  groupedItemsByAnchor: {},
  loading: true,
  error: null,
  bypassCartLogic: false,
  // Active group management for better integration
  currentActiveGroup: null,
  setCurrentActiveGroup: () => {},
  // Flag to identify this as a grouped context for integration with existing hooks
  isGroupedContext: false,
});

export const GroupedPromoModelContext = createContext(defaultGroupedPromoContext);

export const GroupedPromoModelProvider = ({ children, isDisabled, items = {} }) => {
  const isEnableLocalizedPromotion = useConfigService(FEATURE_SWITCH_KEYS.enableLocalizedPromotion) || false;
  const rewardDrawerInitialLength = useConfigService(FEATURE_SWITCH_KEYS.rewardDrawerInitialLength) || 24;
  const { storeId, membershipInformation } = useStore();

  // Use JSON.stringify for stable comparison of items object
  const itemsStr = JSON.stringify(items);
  const itemsKey = useMemo(() => itemsStr, [itemsStr]);

  // State to track which promotion group is currently active/expanded
  const [currentActiveGroup, setCurrentActiveGroupState] = useState(null);

  // Stable setter for current active group
  const setCurrentActiveGroup = useCallback((group) => {
    setCurrentActiveGroupState(group);
  }, []);

  const {
    groupedData = {},
    loading,
    error
  } = useGroupedPromotionProducts({
    items,
    storeId: isEnableLocalizedPromotion ? storeId : null,
    pageSize: rewardDrawerInitialLength,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null,
    skip: isDisabled || items === EMPTY_OBJECT || Object.keys(items).length === 0,
  });

  const contextValue = useMemo(() => Object.freeze({
    isDisabled,
    items, // Original dictionary format: { promotionId: [{itemId, quantity}, ...] }
    // Primary interface: items grouped by anchor ID for cart/business logic
    groupedItemsByAnchor: groupedData,
    loading,
    error,
    bypassCartLogic: true,
    // Active group management for better integration
    currentActiveGroup,
    setCurrentActiveGroup,
    // Flag to identify this as a grouped context for integration with existing hooks
    isGroupedContext: true,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    isDisabled,
    groupedData,
    loading,
    error,
    itemsKey, // Using itemsKey instead of items for stable comparison
    currentActiveGroup,
    setCurrentActiveGroup,
  ]);

  return (
    <GroupedPromoModelContext.Provider value={contextValue}>
      {children}
    </GroupedPromoModelContext.Provider>
  );
};

GroupedPromoModelProvider.displayName = 'GroupedPromoModelProvider';

GroupedPromoModelProvider.propTypes = {
  children: node.isRequired,
  isDisabled: bool,
  items: shape({
    number: arrayOf(shape({
      itemId: string,
      quantity: number,
      promotionId: number
    }))
  }),
};

GroupedPromoModelProvider.defaultProps = {
  isDisabled: false,
  items: {},
};
